import React from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { SectionStage } from '../components/SectionStage';
import { SectionLabel, SectionHeading, PrimaryButton } from '../components/ui/UI';
const architectureSteps = [{
  n: '01',
  title: 'Model Request',
  body: 'The frontier model reasons over approved context and requests a tool call (e.g., "query_evidence_graph" or "request_control_validation").'
}, {
  n: '02',
  title: 'Policy Check',
  body: 'Periscan intercepts the request. The Policy Engine verifies tenant policy, user role, scope, safety level, and approval requirements.'
}, {
  n: '03',
  title: 'Safe Execution',
  body: "Approved requests are executed through Periscan's existing validation modules, API connectors, or internal runners. The model never touches the network directly."
}, {
  n: '04',
  title: 'Redacted Output',
  body: 'Results are captured as evidence. Sensitive data is redacted before the output is returned to the model for further reasoning.'
}];
const modes = [{
  title: 'Plan-Only Mode',
  desc: 'The model can reason and propose validation plans, but cannot execute tools. Ideal for threat advisory analysis and executive summaries.'
}, {
  title: 'Read-Only Evidence',
  desc: 'The model can query Periscan\'s normalized evidence graph to answer questions like "What do we know?" without touching customer systems.'
}, {
  title: 'Safe Validation',
  desc: 'The model can request approved validation missions (e.g., external exposure checks, AI app baseline tests) after policy approval.'
}, {
  title: 'Guided Remediation',
  desc: 'The model can draft Jira tickets, IaC pull requests, or WAF rules in monitor mode. No auto-apply without explicit policy allowance.'
}, {
  title: 'High-Assurance',
  desc: 'For regulated environments. Everything requires human approval. No automatic execution, strict redaction, and detailed audit trails.'
}];
const safetyRules = ['No direct network access.', 'No arbitrary shell commands.', 'No raw secrets sent to the model.', 'All tool calls are policy-checked.', 'All tool calls are audited.', 'All outputs are redacted.', 'All sensitive actions are approval-gated.', 'No risk can be marked fixed without real verification evidence.'];
export function FrontierGateway() {
  return <>
      <PageHero eyebrow="Frontier Gateway" title="Frontier cyber models" titleMuted="need guardrails." description="Periscan lets enterprises use powerful security models without giving them direct access to the network. The model thinks. Periscan controls. Evidence proves." bg="none" data-id="element-1506" />

      {/* Architecture */}
      <SectionStage fx="circuit" data-id="element-1507">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1508">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1509">
            <SectionLabel number="01" label="Architecture" className="mb-8" data-id="element-1510" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-1511">
              <div className="lg:col-span-8" data-id="element-1512">
                <SectionHeading size="xl" data-id="element-1513">
                  The control layer for
                  <br data-id="element-1514" />
                  <span className="text-white/40" data-id="element-1515">AI-driven security.</span>
                </SectionHeading>
              </div>
              <div className="lg:col-span-4 lg:pt-4" data-id="element-1516">
                <p className="text-base text-white/60 leading-relaxed" data-id="element-1517">
                  Frontier Gateway sits between powerful cyber models and your
                  environment. Models can reason and plan, but all interaction
                  goes through typed, policy-controlled Periscan tools.
                </p>
              </div>
            </div>

            <div className="flex flex-col" data-id="element-1518">
              {architectureSteps.map((step, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.04
            }} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-8 border-b border-white/5 last:border-0" data-id="element-1519">
                  <div className="md:col-span-2" data-id="element-1520">
                    <span className="font-mono text-xs text-[#1a8fd4]/60" data-id="element-1521">
                      {step.n}
                    </span>
                  </div>
                  <div className="md:col-span-3" data-id="element-1522">
                    <h3 className="text-xl font-semibold text-white tracking-tight" data-id="element-1523">
                      {step.title}
                    </h3>
                  </div>
                  <div className="md:col-span-7" data-id="element-1524">
                    <p className="text-base text-white/60 leading-relaxed" data-id="element-1525">
                      {step.body}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>
      </SectionStage>

      {/* Modes of Operation */}
      <SectionStage fx="matrix" data-id="element-1526">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1527">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1528">
            <SectionLabel number="02" label="Modes of Operation" className="mb-8" data-id="element-1529" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-1530">
              <div className="lg:col-span-8" data-id="element-1531">
                <SectionHeading size="xl" data-id="element-1532">
                  Bounded autonomy.
                  <br data-id="element-1533" />
                  <span className="text-white/40" data-id="element-1534">
                    You set the leash length.
                  </span>
                </SectionHeading>
              </div>
            </div>

            <div className="border-t border-white/5" data-id="element-1535">
              {modes.map((mode, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.04
            }} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-6 border-b border-white/5 last:border-0" data-id="element-1536">
                  <div className="md:col-span-1" data-id="element-1537">
                    <span className="font-mono text-xs text-[#1a8fd4]/60" data-id="element-1538">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="md:col-span-4" data-id="element-1539">
                    <h3 className="text-lg font-semibold text-white tracking-tight" data-id="element-1540">
                      {mode.title}
                    </h3>
                  </div>
                  <div className="md:col-span-7" data-id="element-1541">
                    <p className="text-base text-white/60 leading-relaxed" data-id="element-1542">
                      {mode.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>
      </SectionStage>

      {/* Safety Rules */}
      <SectionStage fx="constellation" data-id="element-1543">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1544">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1545">
            <SectionLabel number="03" label="Safety & Control" className="mb-8" data-id="element-1546" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-1547">
              <div className="lg:col-span-7" data-id="element-1548">
                <SectionHeading size="xl" data-id="element-1549">
                  The hacker on your side.
                  <br data-id="element-1550" />
                  <span className="text-white/40" data-id="element-1551">
                    With a logbook and a kill switch.
                  </span>
                </SectionHeading>
              </div>
              <div className="lg:col-span-5 lg:pt-4" data-id="element-1552">
                <div className="p-8 rounded-2xl bg-[#1a8fd4]/5 border border-[#1a8fd4]/20" data-id="element-1553">
                  <h4 className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-6" data-id="element-1554">
                    Hard Constraints
                  </h4>
                  <ul className="space-y-4" data-id="element-1555">
                    {safetyRules.map((rule, i) => <li key={i} className="flex items-start gap-3 text-sm text-white/80" data-id="element-1556">
                        <span className="w-1 h-1 rounded-full bg-[#1a8fd4] flex-shrink-0 mt-2" data-id="element-1557" />
                        <span data-id="element-1558">{rule}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-12 flex justify-start" data-id="element-1559">
              <PrimaryButton href="/early-access" size="lg" data-id="element-1560">
                Request Gateway Access
              </PrimaryButton>
            </div>
          </div>
        </section>
      </SectionStage>
    </>;
}